import { DateTime } from 'luxon'

/**
 * Normalises a backend date/timestamp string into the plain `'yyyy-MM-dd'` shape Formik's date
 * fields expect (see `ReviewDateField`, which formats every value it writes the same way).
 *
 * The AI review's `matchedData` dates are backend ISO strings that may carry a time component
 * (e.g. `'2026-09-20T10:00:00.000Z'`). Writing one straight into Formik would put a timestamp in
 * a field the payload mappers and approval-gating logic treat as a plain date. Returns `undefined`
 * when the value is missing or does not parse, so a caller can fall back to whatever was already
 * in the field instead of writing garbage into it.
 *
 * Parsed with the zone pinned to UTC: `DateTime.fromISO` otherwise resolves in the browser's
 * local timezone, so a Z-suffixed midnight timestamp (e.g. `'2024-01-01T00:00:00.000Z'`) would
 * format to the day before for any reviewer west of UTC — most of the US. Pinning to UTC keeps
 * the timestamp's own calendar day regardless of where the reviewer is sitting, and a plain
 * `'yyyy-MM-dd'` value still round-trips unchanged.
 */
export const toFormDateString = (value: string | undefined): string | undefined => {
  if (value === undefined || value === '') {
    return undefined
  }

  const parsed = DateTime.fromISO(value, { zone: 'utc' })

  if (!parsed.isValid) {
    return undefined
  }

  return parsed.toFormat('yyyy-MM-dd')
}

interface ReviewDateBoundsInput {
  dateIssued: string
  expiresAt: string
  /** `'yyyy-MM-dd'`. Injected so tests don't depend on the clock. */
  today?: string
}

interface ReviewDateBounds {
  expirationMin: string
  effectiveMin: string
  effectiveMax: string | undefined
}

const toPlainDate = (value: string): DateTime | null => {
  if (value === '') {
    return null
  }

  const parsed = DateTime.fromISO(value)

  return parsed.isValid ? parsed.startOf('day') : null
}

/**
 * Picker bounds for the review date fields, as `'yyyy-MM-dd'` strings.
 *
 * Expiration and effective dates can't be earlier than today, or than the issue date when that is
 * later. The effective date also can't be later than the expiration date. When the expiration
 * date is itself below that floor (a stale value loaded from the backend), no upper bound is set:
 * a max below the min would disable every day in the effective picker.
 */
export const resolveReviewDateBounds = ({
  dateIssued,
  expiresAt,
  today = DateTime.now().toISODate() ?? '',
}: ReviewDateBoundsInput): ReviewDateBounds => {
  const todayDate = DateTime.fromISO(today).startOf('day')
  const issued = toPlainDate(dateIssued)
  const floor = issued && issued > todayDate ? issued : todayDate
  const expiration = toPlainDate(expiresAt)
  const floorString = floor.toFormat('yyyy-MM-dd')

  return {
    expirationMin: floorString,
    effectiveMin: floorString,
    effectiveMax: expiration && expiration >= floor ? expiration.toFormat('yyyy-MM-dd') : undefined,
  }
}
